import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Card, CardContent, FormControlLabel, Grid, LinearProgress, Radio, RadioGroup } from '@mui/material'
import i18n from 'simple-react-i18n'
import moment from 'moment'
import { isEqual, orderBy } from 'lodash'
import IAEauAction from '../../IAEauAction'
import IAEauModelsResultsChart from './IAEauModelsResultsChart'
import { CardTitle } from '../../../components/card/NewCard'
import { NoDataCard } from '../../../components/card/NoDataCard'
import Select from '../../../components/forms/Select'
import SimpleDatePicker from '../../../components/forms/SimpleDatePicker'
import SimpleCheckbox from '../../../components/forms/SimpleCheckbox'
import NumberField from '../../../components/forms/NumberField'
import { removeHack } from '../../utils/IAEauPredSeriesUtil'
import MUIChartTabs from '../../../components/echart/MUIChartTabs'
import { getSubstractTime } from '../../../components/echart/EChartUtils'
import { HISTO, J30, OTHER } from '../../../alerting/constants/ChartFollowContants'
import { getLocalStorageJson, setLocalStorageJson } from '../../../utils/FormUtils'
import { STATION_TYPE_NAME } from '../../../station/constants/StationConstants'
import PiezometryAction from '../../../piezometry/actions/PiezometryAction'
import HydrometryAction from '../../../hydrometry/actions/HydrometryAction'
import ToastrAction from '../../../toastr/actions/ToastrAction'
import LogAction from '../../../log/actions/LogAction'

const TIME_KEY = 'IAEAU_TAG_RESULTS_TIME'
const OTHER_TIME_KEY = 'IAEAU_TAG_RESULTS_TIME_OTHER'
const RAIN_KEY = 'IAEAU_TAG_RESULTS_RAIN'
const MEDIAN_ZOOM_KEY = 'IAEAU_TAG_RESULTS_MEDIAN_ZOOM'
const HACK_CLICKS = 5
const PIEZO_CHRONIC_TYPE = -1

const DISPLAY_MODE_CLASSIC = 'classic'
const DISPLAY_MODE_J_PLUS = 'jPlus'

const getInitialPeriod = () => {
    const storedTime = getLocalStorageJson(TIME_KEY) || J30
    const time = isNaN(parseInt(storedTime)) ? storedTime : parseInt(storedTime)
    if (time === OTHER) {
        const { minDate, maxDate } = getLocalStorageJson(OTHER_TIME_KEY) ?? {}
        return { time, minDate, maxDate }
    }
    if (time === HISTO) {
        return { time, minDate: undefined, maxDate: moment().endOf('year').valueOf() }
    }
    if (isNaN(parseInt(time))) {
        return { time: J30, minDate: getSubstractTime(J30), maxDate: undefined }
    }
    return { time, minDate: getSubstractTime(time), maxDate: undefined }
}

const getInitialFilters = () => ({
    tag: undefined,
    simulationDate: moment().valueOf(),
    period: getInitialPeriod(),
    withRain: getLocalStorageJson(RAIN_KEY) ?? false,
    withoutHack: false,
    medianZoom: getLocalStorageJson(MEDIAN_ZOOM_KEY) ?? false,
    displayMode: DISPLAY_MODE_CLASSIC,
    jPlus: 0,
})

const IAEauModelsResultsPanel = ({ stationType }) => {
    const dispatch = useDispatch()

    const dataTypes = useSelector(store => stationType === STATION_TYPE_NAME.piezometry ? store.PiezometryReducer.piezometryDataTypes : store.HydrometryReducer.hydrometryDataTypes)

    const [tags, setTags] = useState([])
    // filters : saisie en cours dans la card, appliedFilters : filtres pris en compte par l'écran (mis à jour par « Valider »)
    const [filters, setFilters] = useState(getInitialFilters)
    const [appliedFilters, setAppliedFilters] = useState(filters)
    const [results, setResults] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [titleClicks, setTitleClicks] = useState(0)

    const isJPlusMode = filters.displayMode === DISPLAY_MODE_J_PLUS
    const hasChanges = !isEqual(filters, appliedFilters)

    const changeFilters = changes => setFilters(prev => ({ ...prev, ...changes }))

    useEffect(() => {
        dispatch(stationType === STATION_TYPE_NAME.piezometry ? PiezometryAction.fetchPiezometryDataTypes() : HydrometryAction.fetchHydrometryDataTypes())
        IAEauAction.promiseModelTags(stationType).then(json => setTags(json.map(t => ({ value: t, label: t }))))
    }, [dispatch, stationType])

    const { tag, simulationDate, period, withRain, displayMode, jPlus } = appliedFilters

    useEffect(() => {
        if (!tag) {
            setResults([])
            return () => {}
        }
        const state = { isCancelled: false }
        setIsLoading(true)
        IAEauAction.promiseModelsTagResults(stationType, {
            tag,
            simulationDate: displayMode === DISPLAY_MODE_CLASSIC ? simulationDate : undefined,
            jPlus: displayMode === DISPLAY_MODE_J_PLUS ? jPlus : undefined,
            startDate: period.minDate,
            endDate: period.maxDate,
            withRain,
        })
            .then(json => {
                if (!state.isCancelled) {
                    setResults(orderBy(json, r => r.model.name))
                }
            })
            .catch(err => {
                dispatch(LogAction.logError(`${i18n.fetchError} : ${err}`))
                dispatch(ToastrAction.error(i18n.fetchError))
            })
            .finally(() => {
                if (!state.isCancelled) {
                    setIsLoading(false)
                }
            })
        return () => {
            state.isCancelled = true
        }
    }, [dispatch, stationType, tag, simulationDate, period, withRain, displayMode, jPlus])

    const displayedResults = useMemo(() => appliedFilters.withoutHack ? results.map(removeHack) : results, [results, appliedFilters.withoutHack])

    const getDataType = useCallback(model => {
        const dataType = dataTypes.find(d => d.id === model.typeId)
        if (dataType) {
            return dataType
        }
        return stationType === STATION_TYPE_NAME.piezometry && model.typeId === PIEZO_CHRONIC_TYPE ? { label: `${i18n.chronic} (NGF)`, unit: 'm' } : {}
    }, [dataTypes, stationType])

    const onValidate = () => {
        setLocalStorageJson(RAIN_KEY, filters.withRain)
        setLocalStorageJson(MEDIAN_ZOOM_KEY, filters.medianZoom)
        setAppliedFilters(filters)
    }

    return (
        <Grid container spacing={1}>
            <Grid size={12}>
                <Card>
                    <CardTitle title={<span onClick={() => setTitleClicks(prev => prev + 1)}>{i18n.filters}</span>} color='white' />
                    <CardContent>
                        <Grid container spacing={1} alignItems='center'>
                            <Grid size={2.5}>
                                <Select
                                    label={i18n.tag}
                                    value={filters.tag}
                                    options={tags}
                                    onChange={v => changeFilters({ tag: v })}
                                    obligatory
                                />
                            </Grid>
                            <Grid size={2.5}>
                                <RadioGroup
                                    row
                                    value={filters.displayMode}
                                    onChange={e => changeFilters({ displayMode: e.target.value })}
                                >
                                    <FormControlLabel value={DISPLAY_MODE_CLASSIC} control={<Radio />} label={i18n.classic} />
                                    <FormControlLabel value={DISPLAY_MODE_J_PLUS} control={<Radio />} label='J+X' />
                                </RadioGroup>
                            </Grid>
                            <Grid size={1.5}>
                                <SimpleDatePicker
                                    label={i18n.simulationDate}
                                    value={filters.simulationDate}
                                    onChange={date => changeFilters({ simulationDate: date ?? moment().valueOf() })}
                                    disabled={isJPlusMode}
                                />
                            </Grid>
                            <Grid size={1}>
                                <NumberField
                                    title='J+X'
                                    value={filters.jPlus}
                                    onChange={v => changeFilters({ jPlus: Math.max(parseInt(v) || 0, 0) })}
                                    min={0}
                                    disabled={!isJPlusMode}
                                />
                            </Grid>
                            <Grid size={1.5}>
                                <SimpleCheckbox
                                    label={i18n.displayRain}
                                    checked={filters.withRain}
                                    onToggle={() => changeFilters({ withRain: !filters.withRain })}
                                />
                                {titleClicks >= HACK_CLICKS && (
                                    <SimpleCheckbox
                                        label={i18n.displayWithoutHack}
                                        checked={filters.withoutHack}
                                        onToggle={() => changeFilters({ withoutHack: !filters.withoutHack })}
                                    />
                                )}
                                <SimpleCheckbox
                                    label={i18n.medianZoom}
                                    checked={filters.medianZoom}
                                    onToggle={() => changeFilters({ medianZoom: !filters.medianZoom })}
                                />
                            </Grid>
                            <Grid size={3} container justifyContent='flex-end'>
                                <Button
                                    variant={hasChanges ? 'contained' : 'outlined'}
                                    color={hasChanges ? 'warning' : 'primary'}
                                    onClick={onValidate}
                                >
                                    {i18n.validate}
                                </Button>
                            </Grid>
                            <Grid size={12} container justifyContent='flex-end'>
                                <MUIChartTabs
                                    time={filters.period.time}
                                    onChangeTime={newPeriod => changeFilters({ period: newPeriod })}
                                    stationType={stationType}
                                    timeKey={TIME_KEY}
                                    otherTimeKey={OTHER_TIME_KEY}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>
            {isLoading && (
                <Grid size={12}>
                    <LinearProgress />
                </Grid>
            )}
            <Grid size={12}>
                {results.length ? (
                    <Card>
                        <CardContent>
                            <IAEauModelsResultsChart
                                results={displayedResults}
                                withoutHack={appliedFilters.withoutHack}
                                medianZoom={appliedFilters.medianZoom}
                                jPlus={displayMode === DISPLAY_MODE_J_PLUS ? jPlus : undefined}
                                minDate={period.minDate}
                                maxDate={period.maxDate}
                                getDataType={getDataType}
                            />
                        </CardContent>
                    </Card>
                ) : !isLoading && <NoDataCard message={tag ? i18n.noResults : i18n.selectTag} />}
            </Grid>
        </Grid>
    )
}

IAEauModelsResultsPanel.propTypes = {
    stationType: PropTypes.string.isRequired,
}

export default IAEauModelsResultsPanel
